import { useMemo, type ReactNode } from "react";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import {
  ChevronLeft,
  Activity,
  Cog,
  LayoutGrid,
  LogOut,
  Plus,
  Users,
} from "lucide-react";
import { useMe } from "~/lib/auth";
import { api } from "~/lib/api";
import { useLiveStatus } from "~/lib/live";
import { Mark } from "~/components/Brand/Mark";
import { TabBar, type TabItem } from "~/components/ui/TabBar";

type NavItem = { to: string; label: string; icon: typeof LayoutGrid; adminOnly?: boolean };

const NAV: NavItem[] = [
  { to: "/", label: "Devices", icon: LayoutGrid },
  { to: "/activity", label: "Activity", icon: Activity },
  { to: "/users", label: "Users", icon: Users, adminOnly: true },
  { to: "/account", label: "Settings", icon: Cog },
];

export type AppShellProps = {
  back?: { to?: string; label?: string };
  showTabBar?: boolean;
  rightSlot?: ReactNode;
  children: ReactNode;
  contentClassName?: string;
  bleed?: boolean;
};

export function AppShell({
  back,
  showTabBar = true,
  rightSlot,
  children,
  contentClassName = "",
  bleed = false,
}: AppShellProps) {
  const { data: me } = useMe();
  const isAdmin = me?.role === "admin";

  const tabs = useMemo<TabItem[]>(
    () =>
      NAV
        .filter((n) => !n.adminOnly || isAdmin)
        .map((n) => ({
          to: n.to,
          label: n.label,
          icon: <n.icon size={22} strokeWidth={1.75} />,
        })),
    [isAdmin],
  );

  return (
    <div
      className="min-h-screen md:flex"
      style={{ background: "var(--bg-grouped)" }}
    >
      <Sidebar isAdmin={isAdmin} />

      <div className="flex-1 flex flex-col min-w-0">
        <TopBar back={back} rightSlot={rightSlot} />

        <main
          className={`flex-1 ${bleed ? "" : "px-5 sm:px-6 lg:px-8"} ${contentClassName}`}
          style={{
            paddingBottom: showTabBar
              ? `calc(env(safe-area-inset-bottom) + var(--tabbar-height) + 16px)`
              : `calc(env(safe-area-inset-bottom) + 24px)`,
          }}
        >
          {children}
        </main>
      </div>

      {showTabBar && <TabBar tabs={tabs} />}
    </div>
  );
}

function Sidebar({ isAdmin }: { isAdmin: boolean }) {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const items = NAV.filter((n) => !n.adminOnly || isAdmin);

  return (
    <aside
      className="hidden md:flex md:flex-col shrink-0"
      style={{
        width: 240,
        borderRight: "0.5px solid var(--separator)",
        background: "var(--bg-grouped-secondary)",
      }}
    >
      <div className="px-5 pt-6 pb-4">
        <Link to="/" className="inline-flex items-center gap-2.5">
          <Mark size={28} />
          <span
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 700,
              fontSize: 17,
              letterSpacing: "-0.01em",
              color: "var(--label-primary)",
            }}
          >
            SquaredIoT
          </span>
        </Link>
      </div>

      <nav className="flex-1 px-3 mt-2">
        <ul className="space-y-1">
          {items.map((item) => {
            const Icon = item.icon;
            const active = isNavActive(path, item.to);
            return (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className="flex items-center gap-3 px-3 py-2 transition-colors"
                  style={{
                    borderRadius: 10,
                    background: active ? "var(--fill-tertiary)" : "transparent",
                    color: active ? "var(--label-primary)" : "var(--label-secondary)",
                    fontWeight: active ? 600 : 500,
                    fontSize: 15,
                  }}
                >
                  <Icon
                    size={18}
                    strokeWidth={2}
                    style={{ color: active ? "var(--system-blue)" : "var(--label-secondary)" }}
                  />
                  {item.label}
                </Link>
              </li>
            );
          })}
        </ul>

        <div className="mt-6 px-3">
          <Link
            to="/claim"
            className="flex items-center justify-center gap-2 transition-opacity active:opacity-80"
            style={{
              height: 36,
              borderRadius: 10,
              background: "var(--system-blue)",
              color: "#fff",
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            <Plus size={16} strokeWidth={2.25} />
            Add Device
          </Link>
        </div>
      </nav>

      <LiveStatusBadge />
      <SidebarAccount />
    </aside>
  );
}

function LiveStatusBadge() {
  const status = useLiveStatus();
  const tone =
    status === "open"
      ? { dot: "var(--system-green)", label: "Live", pulse: true }
      : status === "connecting"
        ? { dot: "var(--system-orange)", label: "Reconnecting", pulse: true }
        : { dot: "var(--system-red)", label: "Disconnected", pulse: false };

  return (
    <div
      className="px-3 pt-3"
      style={{ borderTop: "0.5px solid var(--separator)" }}
    >
      <div
        className="flex items-center gap-2 px-2 py-1.5"
        style={{
          borderRadius: 8,
          fontSize: 12,
          color: "var(--label-secondary)",
        }}
        title={
          status === "open"
            ? "Receiving live updates"
            : status === "connecting"
              ? "Trying to reconnect to the live stream"
              : "Live updates stopped — refresh to retry"
        }
      >
        <span
          aria-hidden
          style={{
            width: 7,
            height: 7,
            borderRadius: 999,
            background: tone.dot,
            animation: tone.pulse ? "pulse 1.6s ease-in-out infinite" : undefined,
          }}
        />
        {tone.label}
      </div>
    </div>
  );
}

function SidebarAccount() {
  const { data: me } = useMe();
  const navigate = useNavigate();

  async function signOut() {
    try { await api.logout(); } catch {  }
    navigate({ to: "/login" });
  }

  return (
    <div className="px-3 py-3">
      <div
        className="flex items-center gap-2.5 px-2 py-1.5"
        style={{ borderRadius: 10 }}
      >
        <span
          aria-hidden
          className="grid place-items-center shrink-0"
          style={{
            width: 32,
            height: 32,
            borderRadius: 999,
            background: "var(--fill-secondary)",
            border: "0.5px solid var(--separator)",
            color: "var(--label-primary)",
            fontWeight: 600,
            fontSize: 12,
            letterSpacing: "0.02em",
          }}
        >
          {initials(me?.email)}
        </span>
        <div className="flex-1 min-w-0">
          <div
            className="truncate"
            style={{ fontSize: 13, fontWeight: 500, color: "var(--label-primary)" }}
          >
            {me?.email ?? "—"}
          </div>
          {me?.role && (
            <div className="text-caption-2" style={{ color: "var(--label-tertiary)" }}>
              {capitalize(me.role)}
            </div>
          )}
        </div>
        <button
          type="button"
          aria-label="Sign out"
          onClick={signOut}
          className="grid place-items-center transition-colors"
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            color: "var(--label-tertiary)",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = "var(--system-red)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = "var(--label-tertiary)"; }}
        >
          <LogOut size={15} />
        </button>
      </div>
    </div>
  );
}

function TopBar({
  back,
  rightSlot,
}: {
  back?: { to?: string; label?: string };
  rightSlot?: ReactNode;
}) {
  const navigate = useNavigate();
  const hasBack = !!back;

  if (!hasBack && !rightSlot) {
    return <div aria-hidden style={{ height: "env(safe-area-inset-top)" }} />;
  }

  return (
    <header
      className="sticky top-0 z-20 material-thin"
      style={{
        borderBottom: hasBack ? "0.5px solid var(--separator)" : undefined,
        paddingTop: "env(safe-area-inset-top)",
      }}
    >
      <div className="px-5 sm:px-6 lg:px-8 h-12 flex items-center justify-between gap-2">
        <div className="flex items-center min-w-0">
          {hasBack && (
            <button
              onClick={() => (back!.to ? navigate({ to: back!.to }) : history.back())}
              className="h-9 px-2 -ml-2 flex items-center gap-0.5 text-callout transition-opacity active:opacity-60"
              style={{ color: "var(--system-blue)" }}
              aria-label={back!.label ?? "Back"}
            >
              <ChevronLeft size={22} strokeWidth={2.25} />
              <span>{back!.label ?? "Back"}</span>
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">{rightSlot}</div>
      </div>
    </header>
  );
}

function initials(email: string | undefined): string {
  if (!email) return "?";
  const local = email.split("@")[0] ?? "";
  if (!local) return "?";
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return local.slice(0, 2).toUpperCase();
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function isNavActive(currentPath: string, target: string): boolean {
  if (target === "/") return currentPath === "/" || /^\/[0-9a-f]{8,}/i.test(currentPath);
  return currentPath === target || currentPath.startsWith(target + "/");
}
